import { useMemo, useState } from "react";
import type { TagOut } from "@/lib/projects";
import { setOrderTags } from "@/lib/projectOverview";
import { OverviewCard } from "@/projectOverview/OverviewCard";

export function TagsSection({
  orderId,
  tags,
  suggestions,
  onChanged,
}: {
  orderId: number;
  tags: TagOut[];
  suggestions: string[];
  onChanged: () => void;
}) {
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const names = useMemo(() => tags.map((t) => String(t.name || "").trim()).filter(Boolean), [tags]);

  const ghost = useMemo(() => {
    const q = input.trim().toLowerCase();
    if (!q) return null;
    const hit = suggestions.find((s) => s.toLowerCase().startsWith(q) && s.toLowerCase() !== q);
    return hit || null;
  }, [input, suggestions]);

  async function persist(nextNames: string[]) {
    setSaving(true);
    setError(null);
    try {
      await setOrderTags(orderId, nextNames);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  }

  function addTag(raw: string) {
    const name = raw.trim();
    if (!name) return;
    if (names.some((n) => n.toLowerCase() === name.toLowerCase())) {
      setInput("");
      return;
    }
    void persist([...names, name]);
    setInput("");
  }

  function removeTag(name: string) {
    void persist(names.filter((n) => n !== name));
  }

  return (
    <OverviewCard title="Schlagwörter">
      <div className="mb-2 flex flex-wrap gap-1">
        {names.map((name) => (
          <button
            key={name}
            type="button"
            className="inline-flex items-center gap-1 rounded-full bg-accent-soft px-2 py-0.5 text-xs font-semibold text-accent"
            onClick={() => removeTag(name)}
            title="Entfernen"
          >
            {name}
            <span aria-hidden>×</span>
          </button>
        ))}
        {!names.length ? (
          <span className="text-sm text-label-tertiary">Keine Schlagwörter</span>
        ) : null}
      </div>
      <div className="relative">
        <input
          type="text"
          className="w-full rounded-ios border border-separator bg-card px-3 py-2 text-sm text-label"
          placeholder="Schlagwort eingeben …"
          value={input}
          disabled={saving}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === "Tab") {
              e.preventDefault();
              addTag(ghost && e.key === "Tab" ? ghost : input);
            }
          }}
        />
        {ghost && input ? (
          <span className="pointer-events-none absolute left-3 top-2 text-sm text-label-tertiary">
            <span className="invisible">{input}</span>
            <span>{ghost.slice(input.length)}</span>
          </span>
        ) : null}
      </div>
      {error ? <p className="mt-1 text-xs text-status-red">{error}</p> : null}
    </OverviewCard>
  );
}
